import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {
    ApiKeyValidationResult,
    AuthResult,
    IShineAuthClient,
    LogoutResult,
    RefreshResult,
    TokenValidationResult
} from './shine-auth-client.interface';

/**
 * Options for the Shine Auth client.
 */
export interface ShineAuthOptions {
    /**
     * The base URL of the Shine Auth service.
     */
    baseUrl: string;
}

/**
 * Client for communicating with the Shine Auth service.
 */
@Injectable({
    providedIn: 'root'
})
export class ShineAuthClient implements IShineAuthClient {
    private http = inject(HttpClient);

    private readonly baseUrl: string;

    /**
     * Initializes a new instance of the ShineAuthClient class.
     * @param http The HTTP client.
     */
    constructor() {
        // Use the authBaseServerUrl from the environment if available, otherwise use a default
        this.baseUrl = environment.authBaseServerUrl || 'https://auth.shine.intuvio.com';
    }

    /**
     * Validates an API key.
     * @param apiKey The API key to validate.
     * @returns An observable that resolves to the validation result.
     */
    validateApiKey(apiKey: string): Observable<ApiKeyValidationResult> {
        console.debug(`Validating API key: ${apiKey.substring(0, 4)}...`);

        return this.http.post<any>(`${this.baseUrl}/api/api-keys/validate`, {apiKey})
            .pipe(
                map(response => {
                    if (response?.status !== 'success') {
                        console.warn(`API key validation failed: ${response?.status}`);
                        return {valid: false};
                    }

                    const data = response?.data;
                    return {
                        valid: data?.valid ?? false,
                        clientId: data?.clientId ?? data?.client?.id,
                        permissions: data?.permissions ?? data?.client?.permissions
                    };
                }),
                catchError(error => {
                    console.error('Error validating API key:', error);
                    return of({valid: false});
                })
            );
    }

    /**
     * Validates a JWT token.
     * @param token The JWT token to validate.
     * @returns An observable that resolves to the validation result.
     */
    validateToken(token: string): Observable<TokenValidationResult> {
        console.debug('Validating JWT token');

        return this.http.post<any>(`${this.baseUrl}/api/auth/validate-token`, {token})
            .pipe(
                map(response => {
                    if (response?.status !== 'success') {
                        console.warn(`Token validation failed: ${response?.status}`);
                        return {valid: false};
                    }

                    return {
                        valid: response.data.valid,
                        userId: response.data.payload?.userId,
                        username: response.data.payload?.username,
                        roles: response.data.payload?.roles,
                        clientId: response.data.payload?.clientId
                    };
                }),
                catchError(error => {
                    console.error('Error validating token:', error);
                    return of({valid: false});
                })
            );
    }

    /**
     * Authenticates a user with username and password.
     * @param username The username.
     * @param password The password.
     * @returns An observable that resolves to the authentication result.
     */
    login(username: string, password: string): Observable<AuthResult> {
        console.debug(`Authenticating user: ${username}`);

        return this.http.post<any>(`${this.baseUrl}/api/auth/login`, {username, password})
            .pipe(
                map(response => {
                    if (response?.status !== 'success') {
                        console.warn(`Authentication failed: ${response?.status}`);
                        throw new Error('Authentication failed');
                    }

                    return response.data;
                }),
                catchError(error => {
                    console.error('Error authenticating user:', error);
                    throw error;
                })
            );
    }

    /**
     * Refreshes a JWT token.
     * @param refreshToken The refresh token.
     * @returns An observable that resolves to the refresh result.
     */
    refreshToken(refreshToken: string): Observable<RefreshResult> {
        console.debug('Refreshing token');

        return this.http.post<any>(`${this.baseUrl}/api/auth/refresh-token`, {refreshToken})
            .pipe(
                map(response => {
                    if (response?.status !== 'success') {
                        console.warn(`Token refresh failed: ${response?.status}`);
                        throw new Error('Token refresh failed');
                    }

                    return response.data;
                }),
                catchError(error => {
                    console.error('Error refreshing token:', error);
                    throw error;
                })
            );
    }

    /**
     * Logs out a user.
     * @param refreshToken The refresh token to revoke.
     * @returns An observable that resolves to the logout result.
     */
    logout(refreshToken: string): Observable<LogoutResult> {
        console.debug('Logging out user');

        return this.http.post<any>(`${this.baseUrl}/api/auth/logout`, {refreshToken})
            .pipe(
                map(response => {
                    return {
                        success: response?.status === 'success',
                        message: response?.message || 'Logged out successfully'
                    };
                }),
                catchError(error => {
                    console.error('Error logging out user:', error);
                    return of({
                        success: false,
                        message: error.message || 'Logout failed'
                    });
                })
            );
    }
}
